// TODO: Should this be moved into salvo-ts?

interface Option {
    label: string;
    values: OptionValue[];
}
interface OptionValue {
    label: string;
    value: string;
    selected: boolean;
    /** If this value was picked, the matched variant would be out of stock */
    outOfStock: boolean;
    /** If this value was picked, no matching variant would be selectable */
    variantExists: boolean;
}
type SelectedSet = [OptionValue, OptionValue, OptionValue];
type OptionSet = [Option, Option, Option];
type RawValueSet = [string|null, string|null, string|null];

export abstract class OptionSelector {
    protected product: Shopify.Liquid.Product;
    protected selectedValues: RawValueSet = [null, null, null];
    private variantTree: Record<string, Record<string, Record<string, Shopify.Liquid.Variant>>> = {};

    constructor(product: Shopify.Liquid.Product) {
        this.product = product;
        this.buildVariantTree();
    }

    private buildVariantTree() {
        this.variantTree = {};
        for (const variant of this.product.variants) {
            const option1 = variant.option1 || '';
            const option2 = variant.option2 || '';
            const option3 = variant.option3 || '';

            if (!this.variantTree[option1]) {
                this.variantTree[option1] = {};
            }
            if (!this.variantTree[option1][option2]) {
                this.variantTree[option1][option2] = {};
            }
            this.variantTree[option1][option2][option3] = variant;
        }
    }

    protected findVariant(
        option1: string|null,
        option2: string|null,
        option3: string|null
    ): Shopify.Liquid.Variant|null {
        const branch1 = this.variantTree[option1 || ''] || {};
        const branch2 = branch1[option2 || ''] || {};
        const variant = branch2[option3 || ''] || null;
        return variant;
    }

    public select(option1: string|null, option2: string|null, option3: string|null): void {
        this.selectedValues = [option1, option2, option3];
    }

    public abstract get selected(): SelectedSet;
    public abstract set selected(v: SelectedSet);
    public abstract get options(): OptionSet;
}


export class ManualOptionSelector extends OptionSelector {

    get selected(): SelectedSet {
        const options = this.options;
        return [
            options[0].values.filter((v) => v.value == this.selectedValues[0])[0],
            options[1].values.filter((v) => v.value == this.selectedValues[1])[0],
            options[2].values.filter((v) => v.value == this.selectedValues[2])[0],
        ];
    }
    set selected(set: SelectedSet) {
        this.selectedValues = [
            set[0].value,
            set[1].value,
            set[2].value,
        ];
    }

    get options(): OptionSet {
        const selected = this.selectedValues;

        const [option1Label, option2Label, option3Label] = this.product.options;

        const values: Record<string, OptionValue> = {};
        const option1Values: OptionValue[] = this.product.variants.map((v) => ({
            label: v.option1,
            value: v.option1,
            selected: selected[0] == v.option1,
            outOfStock: false,
            variantExists: !!this.findVariant(v.option1, selected[1], selected[2]),
        }));

        const option2Values: OptionValue[] = this.product.variants.map((v) => ({
            label: v.option2,
            value: v.option2,
            selected: selected[0] == v.option2,
            outOfStock: false,
            variantExists: !!this.findVariant(selected[0], v.option2, selected[2]),
        }));

        const option3Values: OptionValue[] = this.product.variants.map((v) => ({
            label: v.option3,
            value: v.option3,
            selected: selected[0] == v.option3,
            outOfStock: false, // TODO
            variantExists: !!this.findVariant(selected[0], selected[1], v.option3),
        }));

        return [
            {
                label: option1Label,
                values: option1Values,
            },
            {
                label: option2Label,
                values: option2Values,
            },
            {
                label: option3Label,
                values: option3Values,
            }
        ];
    }
}

/**
 * Option selector that attempts to pick the closest variant when an option
 * is updated such that no variant matches the selection.
 */
export class AutoresolvingOptionSelector extends ManualOptionSelector {
    public get wasSelectionModified(): boolean {
        return this._wasSelectionModified;
    }
    private _wasSelectionModified: boolean = false;

    public select(option1: string|null, option2: string|null, option3: string|null): void {
        const oldValues = this.selectedValues;
        super.select(option1, option2, option3);
        this._wasSelectionModified = this.resolveSelection(oldValues, [option1, option2, option3]);
    }

    private resolveSelection(oldValues: RawValueSet, newValues: RawValueSet): boolean {
        if (this.findVariant(newValues[0], newValues[1], newValues[2])) {
            // Variant matches, all okay!
            return false;
        }

        const changes = [
            oldValues[0] != newValues[0],
            oldValues[1] != newValues[1],
            oldValues[2] != newValues[2]
        ];

        const options = this.options;
        let resolved: RawValueSet|null = null;

        if (!resolved && !changes[2]) { // Try to find a better option3
            const option = options[2].values.filter((v) => v.variantExists)[0];
            if (option) {
                resolved = [newValues[0], newValues[1], option.value];
            }
        }
        if (!resolved && !changes[1]) { // Try to find a better option2
            const option = options[1].values.filter((v) => v.variantExists)[0];
            if (option) {
                resolved = [newValues[0], option.value, newValues[2]];
            }
        }
        if (!resolved && !changes[0]) { // Try to find a better option1
            const option = options[0].values.filter((v) => v.variantExists)[0];
            if (option) {
                resolved = [option.value, newValues[1], newValues[2]];
            }
        }

        if (!resolved) {
            return false;
        }

        // Found a better set of options
        this.select(...resolved);
        return true;
    }
}
